import React from 'react';
import config from './config';
import URI from 'urijs';
import constants from './constants';
import { intersection } from 'lodash/array';
import { setAndGetLwaUniqId } from './cookie-utils';
import { logReloginEvent } from './amplitude-utils';
import sessionStorageUtil from './session-storage-util';
import { fetchLogoProps } from './logo-utils';

export const parsedPayload = (searchParams) => (React.useMemo(() => {
  const scopeString = searchParams.get('scope');
  const scopesArray = (scopeString && scopeString.split(/\s+/g)) || [];
  const redirectUrl = searchParams.get('redirect_uri');
  const clientId = searchParams.get('client_id');
  const state = searchParams.get('state');
  const aud = searchParams.get('aud');
  const responseType = searchParams.get('response_type');
  const prompt = searchParams.get('prompt');
  const createAccount = searchParams.has('create');
  const emailToken = searchParams.get('emailToken');
  const registrationToken = searchParams.get('registrationToken');
  const ts = searchParams.get('ts');
  const launch = searchParams.get('launch');
  const idp = searchParams.get('idp');
  const sessionToken = searchParams.get('sessionToken');
  const relogin = searchParams.get('relogin');
  const testApp = searchParams.get('testApp');
  const username = searchParams.get('login_hint');
  const uiLocales = searchParams.get('ui_locales');
  const userId = searchParams.get('amplitude_user_id');
  const amplitudeDeviceId = searchParams.get('amplitude_device_id');

  let portalSettings = {};
  try {
    const parsedState = JSON.parse(state);
    const { PORTALREDIRECTURL, PRACTICEID, COMMUNICATORBRANDID, LANGUAGE } = parsedState;
    portalSettings = { PORTALREDIRECTURL, PRACTICEID, COMMUNICATORBRANDID, LANGUAGE };
  } catch (err) {
  }
  const fhir = isFhirScopeRequested(scopesArray);
  function isFhirScopeRequested(scopes) {
    return intersection(config.fhirScopes, scopes).length > 0;
  }

  function isFhirEhrLaunchRequested(scopes) {
    return intersection(config.fhirEhrLaunchScopes, scopes).length > 0;
  }
  const isFhirEhrLaunch = isFhirEhrLaunchRequested(scopesArray) && !!launch;
  const authorizeRedirectUrl = new URI(window.location.href).origin(config.oktaURL);
  const originalRedirectUri = new URI(window.location.href).removeQuery('prompt', 'login');
  const authServerId = authorizeRedirectUrl.segment()[1];
  const lwaUniqId = setAndGetLwaUniqId();
  return {
    clientId: clientId,
    scopesArray,
    scopeString,
    redirectUri: redirectUrl,
    aud,
    prompt,
    emailToken,
    registrationToken,
    ts,
    launch,
    portalSettings,
    createAccount,
    isFhirEhrLaunch,
    fhir,
    idp,
    authorizeRedirectUrl,
    sessionToken,
    relogin,
    testApp,
    authServerId,
    responseType,
    username,
    state,
    lwaUniqId,
    userId,
    amplitudeDeviceId,
    originalRedirectUri,
    uiLocales,
  };
}, [ searchParams ]));


export function enrichApiResponse({ apiResponse, parsedPayload }) {
  const { authorizeRedirectUrl, fhir, sessionToken, testApp, authServerId, createAccount,
          clientId, aud, state, username, originalRedirectUri } = parsedPayload;
  const idp = apiResponse.idp || parsedPayload.idp;
  const usernameResponse = apiResponse.username;
  let workflow;
  if (idp) {
    authorizeRedirectUrl.removeQuery('prompt', 'login').addQuery({ idp });
    workflow = constants.AUTHN_WORKFLOWS.AUTHORIZEVIAIDP;
  } else {
    // for non-federated cases, we provide our own login experience, so we override some parameters related to that,
    // but for the federated case we want to let Okta handle login prompting behavior
    authorizeRedirectUrl.removeQuery('prompt', 'login').removeQuery('login_hint');
    if (fhir) {
      workflow = constants.AUTHN_WORKFLOWS.FHIRAUTHORIZE;
    } else {
      workflow = constants.AUTHN_WORKFLOWS.AUTHORIZE;
    }
  }
  const postAuthnRoutes = JSON.stringify(apiResponse.postProcessingSteps.map((step) => {
    if (step.needsSanitize) {
      return { route: 'account/sanitize' };
    }
    if (step.workflow) {
      return { route: 'workflow' };
    }
    if (step.authorizeToOkta) {
      return { url: authorizeRedirectUrl.toString() };
    }
  }).filter(( ele ) => !!ele ));
  const prompt = idp ? undefined : parsedPayload.prompt;
  const proxiedAuthorizeValues = { workflow, originalRedirectUri: originalRedirectUri.toString(), fhir, sessionToken,
                                   testApp, authServerId, idp, prompt: parsedPayload.prompt,
                                   // eslint-disable-next-line camelcase
                                   profile: false, login_hint: usernameResponse };
  return { ...apiResponse, idp, postAuthnRoutes,
           proxiedAuthorizeValues: JSON.stringify(proxiedAuthorizeValues), createAccount, clientId, aud, state,
           prompt, username: apiResponse.username || username, workflow, ...fetchLogoProps(idp) };


}

/**
 * Check if relogin attempted already, attempts if not
 * @param cause {string} - Reason for performing the relogin, so it gets logged to amplitude
 * @return {Promise<boolean>} Indicates if the relogin is being performed
 */
export async function performReloginIfAllowed({ cause, message }){
  const originalAuthUrl = sessionStorageUtil.getOriginalAuthUrl();
  if (originalAuthUrl) {
    const parsedUrl = new URL(originalAuthUrl);
    if (!parsedUrl.searchParams.get('relogin')) {
      parsedUrl.searchParams.append('relogin', '1');
      await logReloginEvent({ cause, message });
      window.location.replace(parsedUrl.toString());
      return true;
    }
  }
  return false;
}

/**
 * Get username from session if exist
 * @param {authClient} OktaAuth from @okta/okta-react
 * @returns {Promise<undefined|string>}
 */
export async function getUsernameFromSession(oktaAuth) {
  const { login } = await oktaAuth.session.get();
  return login;
}
